import { FC, useState } from "react";
import { dismissPrompt, readPromptDismissed } from "../../../shared/lib/push";
import { usePushNotifications } from "../model/usePushNotifications";

/**
 * A one-shot nudge above the page content — the only banner the app shows.
 *
 * Appears in `default`, `requesting` and `error` — never to re-ask a denied browser, never
 * once subscribed. It used to unmount the instant `state` left `default`, which made the tap
 * target disappear out from under the user while the native permission dialog was still open,
 * and left a failed subscribe with no visible retry until the next app open. Staying mounted
 * through `requesting`/`error` fixes both without adding a second enable surface.
 *
 * Dismissal is remembered for good, per user: there is deliberately no other enable surface in
 * the app, so closing this banner means push stays off for that user on this device. The user id
 * comes in as a prop because a feature may not import another feature (auth).
 *
 * Never shown once the browser has granted permission — the registrar subscribes silently then.
 *
 * On iOS it stays out of the way entirely: push needs the app installed first, and
 * installing lives behind "App installieren" in the burger menu.
 */
export const PushBanner: FC<{ userId: string }> = ({ userId }) => {
  const { state, enable } = usePushNotifications();
  const [isDismissed, setIsDismissed] = useState(() => readPromptDismissed(userId));

  if ((state !== "default" && state !== "requesting" && state !== "error") || isDismissed) {
    return null;
  }

  const isRequesting = state === "requesting";

  const handleDismiss = () => {
    dismissPrompt(userId);
    setIsDismissed(true);
  };

  return (
    <div className="mx-4 mt-4 flex items-center gap-3 rounded-xl border border-[#C9D8EE] bg-[#E6F0FB] px-4 py-3">
      <img src="/icons/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold leading-tight text-[#124093]">
          Benachrichtigungen einschalten
        </p>
        <p className="text-[14px] leading-tight text-[#4A5C74]">
          {state === "error"
            ? "Hat nicht geklappt — bitte erneut versuchen."
            : "Termine und Aufgaben direkt auf dem Handy."}
        </p>
      </div>
      <button
        type="button"
        onClick={() => void enable()}
        disabled={isRequesting}
        className="shrink-0 rounded-lg bg-[#124093] px-4 py-2 text-[15px] font-bold text-white disabled:opacity-60"
      >
        {isRequesting ? "Wird aktiviert…" : state === "error" ? "Erneut versuchen" : "Aktivieren"}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Hinweis ausblenden"
        className="shrink-0 rounded p-1 text-[#4A5C74] hover:bg-white/60"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden>
          <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};
